import { create } from 'zustand'
import { authApi } from '../services/api'
import type { User } from '../types'

interface AuthState {
  user: User | null
  isLoading: boolean
  isCheckingAuth: boolean
  error: string | null

  // Actions
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  checkAuth: () => Promise<void>
  register: (email: string, password: string, name: string, company?: string) => Promise<void>
  clearError: () => void
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isLoading: false,
  isCheckingAuth: true,
  error: null,

  login: async (email: string, password: string) => {
    set({ isLoading: true, error: null })
    try {
      const user = await authApi.login(email, password)
      set({ user, isLoading: false })
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } }
      set({
        error: error.response?.data?.error || 'Login failed',
        isLoading: false,
      })
      throw err
    }
  },

  logout: async () => {
    try {
      await authApi.logout()
    } finally {
      set({ user: null })
    }
  },

  checkAuth: async () => {
    set({ isCheckingAuth: true })
    try {
      const user = await authApi.getMe()
      set({ user, isCheckingAuth: false })
    } catch {
      set({ user: null, isCheckingAuth: false })
    }
  },

  register: async (email: string, password: string, name: string, company?: string) => {
    set({ isLoading: true, error: null })
    try {
      const user = await authApi.register(email, password, name, company)
      set({ user, isLoading: false })
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } }
      set({
        error: error.response?.data?.error || 'Registration failed',
        isLoading: false,
      })
      throw err
    }
  },

  clearError: () => set({ error: null }),
}))

// Check auth on app load
useAuthStore.getState().checkAuth()
